import produce from "immer";
import { cloneDeep, has, omit } from "lodash";
import { abs, chain, round } from "mathjs";
import * as moment from "moment";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { convertBStoAD } from "../../components/Calendar/functions/calendarFunctions";
import { paymentOptions, paymentOptionsEnum } from "../../containers/Billing/Editor/BillSummary";
import { SettingsType } from "../../containers/ResourceCentre/Settings";
import { numberToWords } from "../../helpers/rupee";
import { TrackingNumType } from "../../interfaces/Accounts";
import {
  BatchInfoType,
  BillDocumentType,
  BillItemFieldTypes,
  BillItemSource,
  BillItemType,
  BillPaymentInfoType,
  BillSummaryType,
  BillType,
  CurrentBillVersion,
  DiscountBasedOn,
  DiscountTypes,
  DocumentTypes,
  PaymentBreakdown,
  RefundInfoType,
  StockUnitTypes,
  VisitType
} from "../../interfaces/BillInterfaces";
import { Client } from "../../interfaces/ClientInterface";
import { ProductInterface } from "../../interfaces/ProductInterface";
import { StockProducts } from "../../interfaces/StockInterfaces";
import { UserContext } from "../../interfaces/UserContext";
import { ReverseMap } from "../../helpers/types";
import { scaleFont } from "../../components/Print/Print";
import { MEDICAL_SUB_PRODUCT, SCHEME_TYPE } from "../../../map/ssf";
import { SSF_MEDICINE_CODE } from "../../models/ssf";

export const defaultDiscountSettings: {
  discountBasis: DiscountBasedOn;
  discountType: DiscountTypes;
} = {
  discountBasis: DiscountBasedOn.invoice,
  discountType: DiscountTypes.percentage
};

export const isBillCreatedBefore24HoursAgo = (bill: BillType): boolean =>
  moment(bill.created_at).isBefore(moment().subtract(24, "hours"));

export const isOldFormatBill = (billVersion = ""): boolean => {
  const currentVersionInParts = CurrentBillVersion.split(".").map((versionNumber) =>
    Number(versionNumber)
  );
  const oldVersionInParts = billVersion.split(".").map((versionNumber) => Number(versionNumber));
  for (let index = 0; index < currentVersionInParts.length; index += 1) {
    if (currentVersionInParts[index] > oldVersionInParts[index]) return true;
    // eslint-disable-next-line no-continue
    if (currentVersionInParts[index] === oldVersionInParts[index]) continue;
    else return false;
  }
  return false;
};

export enum BillColumns {
  SNO = "sNo",
  hsCode = "hsCode",
  DESCRIPTION = "description",
  SERVICE_PROVIDER = "serviceProvider",
  INFO = "info",
  QUANTITY = "quantity",
  PER_UNIT = "perUnit",
  DISCOUNT_AMT = "discountAmt",
  VAT_PCT = "vatPct",
  GROSS_TOTAL = "grossTotal"
}

export type BillColumnsType = ReverseMap<typeof BillColumns>;

export const getBillColumns = (showInfo: boolean, showSp?: boolean): BillColumns[] => [
  BillColumns.SNO,
  BillColumns.hsCode,
  BillColumns.DESCRIPTION,
  ...(showInfo ? [BillColumns.INFO] : []),
  ...(showSp ? [BillColumns.SERVICE_PROVIDER] : []),
  BillColumns.QUANTITY,
  BillColumns.PER_UNIT,
  BillColumns.DISCOUNT_AMT,
  BillColumns.VAT_PCT,
  BillColumns.GROSS_TOTAL
];

export const getTemplateBatchInfo = (): BatchInfoType => ({
  batchId: "",
  quantity: 1,
  expiryDate: null
});

export const getTemplateBillSummary = (): BillSummaryType => ({
  taxableAmount: 0,

  // Existing fields for overall (VAT + HET)
  taxAmount: 0,
  taxableSales: 0,
  taxAmtWithRates: {},

  // New fields for VAT only
  vatAmount: 0,
  vatTaxableSales: 0,
  vatAmtWithRates: {},

  // Existing common fields
  totalPerUnitTimesQty: 0,
  totalAmount: 0,
  inWords: "zero rupees",
  discount: 0,
  discountPct: 0,
  discountType: null,
  roundOffAmt: 0,
  exemptSales: 0
});

export const getTemplateBillItem = (): Partial<BillItemType> => ({
  sNo: 1,
  uuid: uuidv4(),
  delivered: true,
  description: "",
  serviceProviderId: null,
  serviceProviderIds: [],
  batchInfo: getTemplateBatchInfo(),
  quantity: 1,
  unit: "pcs",
  perUnit: 0,
  discountPercent: 0,
  discountAmt: 0,
  vatPct: 0,
  vatAmtAfterDiscount: 0,
  vatAmtBeforeDiscount: 0,
  grossTotal: 0,
  serviceProviderRateUnit: "percentage",
  stockUnitType: StockUnitTypes.unit,
  source: BillItemSource.services,
  info: ""
});

export const getDiscountField = (discountSettings: {
  discountBasis: DiscountBasedOn;
  discountType: DiscountTypes;
}): BillItemFieldTypes | null => {
  if (discountSettings?.discountBasis === DiscountBasedOn.inline) {
    if (discountSettings?.discountType === DiscountTypes.percentage)
      return BillItemFieldTypes.discountPercent;
    return BillItemFieldTypes.discountAmt;
  }

  return null;
};

export enum INSURANCE_TYPE {
  SSF = "ssf"
}

export const createTemplateDraft = (
  userContext: UserContext,
  client: Client = { id: null } as Client,
  rcSettings: SettingsType | Record<string, unknown>,
  isProformaBill: boolean,
  isSsfBilling?: boolean
): BillDocumentType => {
  const { billingSettings } = rcSettings || userContext.resourceCentre.settings;
  const isDepartmentSubscribed =
    userContext.resourceCentre?.subscriptions?.features?.department?.subscribed || false;

  return {
    visitType: VisitType.OPD,
    version: CurrentBillVersion,
    type: DocumentTypes.INVOICE,
    insuranceType: isSsfBilling && INSURANCE_TYPE.SSF,
    isDraft: true,
    isInternal: userContext.resourceCentre.settings?.billingSettings?.billingType === "internal",
    title: "bill",
    billNumber: null,
    referredBy: "",
    issueDate: moment().toISOString(),
    dueDate: null,
    remarks: billingSettings?.defaultRemarks || "",
    fiscalYear: "",
    isSsfBilling: false,
    ssf: {
      id: undefined,
      uuid: null,
      scheme: SCHEME_TYPE.MEDICAL,
      subProduct: MEDICAL_SUB_PRODUCT.OPD,
      balance: {
        medical: { opd: 0, ipd: 0, isActive: false },
        accident: { value: 0, isActive: false }
      }
    },
    summary: getTemplateBillSummary(),
    billItems: [getTemplateBillItem()],
    client,
    resourceCentre: {
      id: userContext.resourceCentre.id,
      panNo: billingSettings?.panNo
    },
    settings: {
      showFields: [
        billingSettings?.extraColumns.delivered && BillItemFieldTypes.delivered,
        BillItemFieldTypes.description,
        billingSettings?.extraColumns?.type && BillItemFieldTypes.type,
        isDepartmentSubscribed &&
          billingSettings?.extraColumns.department &&
          BillItemFieldTypes.department,
        billingSettings?.extraColumns?.quantity && BillItemFieldTypes.quantity,
        billingSettings?.extraColumns?.unit && BillItemFieldTypes.unit,
        BillItemFieldTypes.perUnit,
        BillItemFieldTypes.discountPercent,
        billingSettings?.extraColumns?.info && BillItemFieldTypes.info,
        BillItemFieldTypes.discountAmt,
        billingSettings?.extraColumns.vat && BillItemFieldTypes.VATPercent,
        BillItemFieldTypes.grossTotal
      ].filter((val) => val),
      discountSettings: billingSettings?.discountSettings
        ? billingSettings.discountSettings
        : defaultDiscountSettings,
      remarksCompulsoryForDiscountedBill: Boolean(
        billingSettings?.remarksCompulsoryForDiscountedBill
      ),
      extraColumnSettings: {
        ...billingSettings?.extraColumns
      }
    },
    enteredBy: {
      userId: userContext.userCreds.id
    } as BillDocumentType["enteredBy"],
    paymentInfo: {
      paid: !isProformaBill,
      // initially 0. paidAmount is assumed to be
      // equal to summary.totalAmount when paid is true
      paidAmount: 0,
      tenderAmount: 0,
      changeAmount: 0,
      paymentBreakdown: [
        {
          method: isProformaBill ? paymentOptionsEnum.noPayment : paymentOptionsEnum.cash,
          amount: 0
        }
      ],

      paymentDistribution: {
        paymentFromBalance: 0,
        additionalPayment: 0,
        paymentFromSsf: 0
      }
    },
    remindOn: {
      format: "days",
      duration: 0,
      note: ""
    },
    referenceTo: null,
    isProformaBill
  };
};

const findDiscountPct = (
  discountBasedOn: DiscountBasedOn | string,
  summary: BillSummaryType,
  discount: number
) => {
  if (discountBasedOn === DiscountBasedOn.invoice) {
    return summary.discountPct;
  }
  return round(discount, 2);
};

const findDiscountAmtDisTypePct = (
  discountBasedOn: DiscountBasedOn | string,
  summary: BillSummaryType,
  discount: number,
  quantityTimesPerUnitPrice: number,
  totalExcNonDiscountProduct: number
) => {
  if (discountBasedOn === DiscountBasedOn.invoice) {
    return totalExcNonDiscountProduct
      ? round(quantityTimesPerUnitPrice * (summary.discount / totalExcNonDiscountProduct), 2) || 0
      : 0;
  }
  return round(chain(quantityTimesPerUnitPrice).multiply(discount).divide(100).done(), 2) || 0;
};

const findDiscountAmtDisTypeAmt = (
  discountBasedOn: DiscountBasedOn | string,
  discount: number,
  quantityTimesPerUnitPrice: number,
  totalExcNonDiscountProduct: number
) => {
  if (discountBasedOn === DiscountBasedOn.invoice) {
    return totalExcNonDiscountProduct
      ? round(quantityTimesPerUnitPrice * (discount / totalExcNonDiscountProduct), 2) || 0
      : 0;
  }
  return round(discount, 2) || 0;
};

const findTotalExcNonDiscountProduct = (draft) => {
  const netTotalExcNonDiscountProduct = draft.billItems.reduce((acc, billItem) => {
    if (!billItem.nonDiscountable) {
      return acc + billItem.perUnit * billItem.quantity;
    }
    return acc;
  }, 0);
  return netTotalExcNonDiscountProduct;
};

const calculateIntermediateBillItemValues = ({
  perUnitPrice = 0,
  quantity = 1,
  vatPct = 0,
  discountType = DiscountTypes.percentage,
  discount = 0,
  nonDiscountable = false,
  summary = getTemplateBillSummary(),
  discountBasedOn = "",
  totalExcNonDiscountProduct = 0
}: {
  perUnitPrice?: number;
  quantity?: number;
  vatPct?: number;
  discountType?: DiscountTypes;
  discount?: number;
  nonDiscountable?: boolean;
  summary?: BillSummaryType;
  discountBasedOn?: DiscountBasedOn | string;
  totalExcNonDiscountProduct?: number;
}) => {
  let discountAmt = 0;
  let discountPercent = 0;
  const quantityTimesPerUnitPrice = chain(perUnitPrice).multiply(quantity).done();

  if (discountType === DiscountTypes.percentage) {
    discountAmt = !nonDiscountable
      ? findDiscountAmtDisTypePct(
          discountBasedOn,
          summary,
          discount,
          quantityTimesPerUnitPrice,
          totalExcNonDiscountProduct
        )
      : 0;
    discountPercent = !nonDiscountable ? findDiscountPct(discountBasedOn, summary, discount) : 0;
  } else {
    discountAmt = !nonDiscountable
      ? findDiscountAmtDisTypeAmt(
          discountBasedOn,
          discount,
          quantityTimesPerUnitPrice,
          totalExcNonDiscountProduct
        )
      : 0;
    const dis = quantityTimesPerUnitPrice
      ? round(chain(discount).multiply(100).divide(quantityTimesPerUnitPrice).done(), 2) || 0
      : 0;

    discountPercent = !nonDiscountable ? findDiscountPct(discountBasedOn, summary, dis) : 0;
  }

  // tax calculations
  const hasVat = Number(vatPct) > 0;

  // discounted price
  const discountedPrice = round(chain(quantityTimesPerUnitPrice).subtract(discountAmt).done(), 2);

  // vat related calculations
  const vatAmtBeforeDiscount = round(
    chain(quantityTimesPerUnitPrice).multiply(vatPct).divide(100).done(),
    2
  );
  const vatAmtAfterDiscount =
    round(chain(discountedPrice).multiply(vatPct).divide(100).done(), 2) || 0;

  // gross total calculations
  const grossTotalWithDiscount =
    round(
      chain(discountedPrice)
        .add(hasVat ? vatAmtAfterDiscount : 0)
        .done(),
      2
    ) || 0;

  const grossTotalWithoutDiscount =
    round(
      chain(quantityTimesPerUnitPrice)
        .add(hasVat ? vatAmtBeforeDiscount : 0)
        .done(),
      2
    ) || 0;

  return {
    discountPercent,
    discountAmt,
    discountedPrice,
    vatAmtBeforeDiscount,
    vatAmtAfterDiscount,
    grossTotalWithDiscount,
    grossTotalWithoutDiscount
  };
};

export function calculateBillItems(draft: BillDocumentType): BillDocumentType {
  let { discount, discountPct } = draft.summary;
  const { discountType } = draft.settings.discountSettings;

  let totalPriceExcVAT = 0;
  let fragmentedDiscountSum = 0;

  if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.invoice) {
    totalPriceExcVAT = draft.billItems.reduce(
      (acc, billItem) =>
        chain(billItem.perUnit || 0)
          .multiply(billItem.quantity || 1)
          .add(acc || 0)
          .done(),
      0
    );

    if (discountType === DiscountTypes.percentage)
      discount = round(
        chain(totalPriceExcVAT)
          .multiply(discountPct || 0)
          .divide(100)
          .done(),
        2
      );
    else
      discountPct = totalPriceExcVAT
        ? round(chain(discount).divide(totalPriceExcVAT).multiply(100).done(), 2) || 0
        : 0;
  }

  const billItems = draft.billItems.map((billItem) => {
    const newBillItem = produce(billItem, (draftItem) => {
      draftItem.perUnit = round(draftItem.perUnit || 0, 2);
      draftItem.quantity = draftItem.quantity ?? 1;
      draftItem.hsCode = draftItem?.hsCode || "";

      // draftItem.delivered = draft.settings.showFields.includes(BillItemFieldTypes.delivered);
      if (!draft.settings.showFields.includes(BillItemFieldTypes.department)) {
        draftItem.departmentId = undefined;
      }

      if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.inline) {
        if (draft.settings.discountSettings.discountType === DiscountTypes.percentage) {
          draftItem.discountPercent = draftItem.discountPercent || 0;
          const { discountAmt, vatAmtBeforeDiscount, vatAmtAfterDiscount, grossTotalWithDiscount } =
            calculateIntermediateBillItemValues({
              perUnitPrice: draftItem.perUnit,
              quantity: draftItem.quantity,
              vatPct: draftItem.vatPct,
              discountType: draft.settings.discountSettings.discountType,
              discount: draftItem.discountPercent,
              nonDiscountable: draftItem.nonDiscountable
            });
          draftItem.discountAmt = discountAmt;

          draftItem.vatAmtAfterDiscount = vatAmtAfterDiscount;
          draftItem.vatAmtBeforeDiscount = vatAmtBeforeDiscount;

          draftItem.grossTotal = grossTotalWithDiscount;
        } else {
          draftItem.discountAmt = draftItem.discountAmt || 0;
          const {
            discountPercent,
            vatAmtBeforeDiscount,
            vatAmtAfterDiscount,
            grossTotalWithDiscount
          } = calculateIntermediateBillItemValues({
            perUnitPrice: draftItem.perUnit,
            quantity: draftItem.quantity,
            vatPct: draftItem.vatPct,
            discountType: draft.settings.discountSettings.discountType,
            discount: draftItem.discountAmt,
            nonDiscountable: draftItem.nonDiscountable
          });
          draftItem.discountPercent = discountPercent;

          draftItem.vatAmtAfterDiscount = vatAmtAfterDiscount;
          draftItem.vatAmtBeforeDiscount = vatAmtBeforeDiscount;

          draftItem.grossTotal = grossTotalWithDiscount;
        }
      } else {
        // proportinately subtract the discount from individual item &
        // if required randomly re-conciliate on one item
        // (total inc. vat - disount != discounedTotal with vat)
        const totalExcNonDiscountProduct = findTotalExcNonDiscountProduct(draft);
        const {
          discountPercent,
          discountAmt,
          vatAmtBeforeDiscount,
          vatAmtAfterDiscount,
          grossTotalWithoutDiscount
        } = calculateIntermediateBillItemValues({
          perUnitPrice: draftItem.perUnit,
          quantity: draftItem.quantity,
          vatPct: draftItem.vatPct,
          discountType: DiscountTypes.percentage,
          discount: discountPct,
          nonDiscountable: draftItem.nonDiscountable,
          summary: draft.summary,
          discountBasedOn: DiscountBasedOn.invoice,
          totalExcNonDiscountProduct
        });
        draftItem.discountAmt = discountAmt;
        draftItem.discountPercent = discountPercent;

        draftItem.vatAmtAfterDiscount = vatAmtAfterDiscount;
        draftItem.vatAmtBeforeDiscount = vatAmtBeforeDiscount;

        draftItem.grossTotal = grossTotalWithoutDiscount;
      }
      // billItem.grossTotal = round(grossTotal, 2);
    });

    fragmentedDiscountSum = Number(
      chain(fragmentedDiscountSum)
        .add(newBillItem?.discountAmt ?? 0)
        .done()
    );
    return newBillItem;
  });

  // reconciliation for discount on first bill item
  if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.invoice) {
    if (fragmentedDiscountSum !== discount && billItems[0])
      billItems[0] = produce(billItems[0], (draftItem) => {
        const {
          discountPercent,
          discountAmt,
          vatAmtBeforeDiscount,
          vatAmtAfterDiscount,
          grossTotalWithoutDiscount
        } = calculateIntermediateBillItemValues({
          perUnitPrice: draftItem.perUnit,
          quantity: draftItem.quantity,
          vatPct: draftItem.vatPct,
          discountType: DiscountTypes.amount,
          discount: Number(
            chain(discount ?? 0)
              .subtract(fragmentedDiscountSum)
              .add(draftItem?.discountAmt ?? 0)
              .done()
          ),
          nonDiscountable: draftItem.nonDiscountable
        });
        draftItem.discountAmt = discountAmt;
        draftItem.discountPercent = discountPercent;

        draftItem.vatAmtAfterDiscount = vatAmtAfterDiscount;
        draftItem.vatAmtBeforeDiscount = vatAmtBeforeDiscount;

        draftItem.grossTotal = grossTotalWithoutDiscount;
      });
  }
  return { ...draft, billItems };
}

export function calculateSummary(draft: BillDocumentType): BillDocumentType {
  const summary: BillSummaryType = {
    taxableAmount: 0,

    // Existing fields for overall (VAT + HET)
    taxAmount: 0,
    taxableSales: 0,
    taxAmtWithRates: {},

    // New fields for VAT only
    vatAmount: 0,
    vatTaxableSales: 0,
    vatAmtWithRates: {},

    // other common values
    totalPerUnitTimesQty: 0,
    totalAmount: 0,
    inWords: "",
    roundOffAmt: draft.summary?.roundOffAmt || 0,
    discount: 0,
    discountPct: 0,
    exemptSales: 0
  };

  let totalBillItemPrice = 0;

  /**
   * Calculate bill item totals and aggregated tax/VAT/HET values.
   * Loops through bill items and computes per-item and overall totals
   * - Overall values: totalBillItemPrice, exemptSales
   * - Overall (all taxes): taxableSales, taxAmount, taxAmtWithRates
   * - VAT-only: vatAmount, vatTaxableSales, vatAmtWithRates
   */

  draft.billItems.forEach((billItem) => {
    const quantityTimesPerUnitPrice = Number(
      chain(billItem.perUnit || 0)
        .multiply(billItem.quantity || 1)
        .done()
    );

    totalBillItemPrice = round(chain(totalBillItemPrice).add(quantityTimesPerUnitPrice).done(), 2);

    const hasVat = Number(billItem.vatPct ?? 0) > 0;

    if (hasVat) {
      // taxable sales
      summary.taxableSales += quantityTimesPerUnitPrice;
      summary.vatTaxableSales += quantityTimesPerUnitPrice;

      // tax amount
      const vatAmt = billItem.vatAmtAfterDiscount || 0;
      summary.taxAmount = round(chain(summary.taxAmount).add(vatAmt).done(), 2);
      summary.vatAmount = round(chain(summary.vatAmount).add(vatAmt).done(), 2);

      // tax by rate
      const vatPct = billItem.vatPct ?? 0;
      summary.taxAmtWithRates[vatPct] =
        round(
          chain(summary.taxAmtWithRates[vatPct] || 0)
            .add(vatAmt)
            .done(),
          2
        ) || 0;

      summary.vatAmtWithRates[vatPct] =
        round(
          chain(summary.vatAmtWithRates[vatPct] || 0)
            .add(vatAmt)
            .done(),
          2
        ) || 0;
    } else {
      summary.exemptSales = round(
        chain(summary.exemptSales).add(quantityTimesPerUnitPrice).done(),
        2
      );
    }
  });

  const netTotalExcNonDiscountProduct = findTotalExcNonDiscountProduct(draft);

  if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.invoice) {
    summary.discountPct =
      // eslint-disable-next-line no-nested-ternary
      draft.settings.discountSettings.discountType === DiscountTypes.percentage
        ? draft.summary.discountPct || 0
        : netTotalExcNonDiscountProduct
        ? round(
            chain(draft.summary.discount)
              .divide(netTotalExcNonDiscountProduct)
              .multiply(100)
              .done(),
            2
          ) || 0
        : 0;

    summary.discount =
      draft.settings.discountSettings.discountType === DiscountTypes.amount
        ? draft.summary.discount || 0
        : round(
            chain(draft.summary.discountPct || 0)
              .divide(100)
              .multiply(netTotalExcNonDiscountProduct)
              .done(),
            2
          ) || 0;
  } else {
    const inlineDiscountSum = draft.billItems.reduce(
      (acc, billItem) => acc + (billItem?.discountAmt ?? 0),
      0
    );

    summary.discount = inlineDiscountSum || 0;
    summary.discountPct = totalBillItemPrice
      ? round(chain(inlineDiscountSum).divide(totalBillItemPrice).multiply(100).done(), 2) || 0
      : 0;
  }

  summary.totalPerUnitTimesQty = totalBillItemPrice;
  summary.taxableAmount = round(chain(totalBillItemPrice).subtract(summary.discount).done(), 2);

  if (localStorage.getItem("automaticallyCalculateRoundOffAmount") === "true") {
    const totalAmountWithoutRoundoff = round(
      chain(totalBillItemPrice).subtract(summary.discount).add(summary.taxAmount).done(),
      2
    );

    summary.totalAmount = round(totalAmountWithoutRoundoff, 2);
    summary.roundOffAmt = round(
      chain(summary.totalAmount).subtract(totalAmountWithoutRoundoff).done(),
      2
    );
  } else {
    summary.totalAmount = round(
      chain(totalBillItemPrice)
        .subtract(summary.discount)
        .add(summary.taxAmount)
        .add(summary.roundOffAmt)
        .done(),
      2
    );
  }

  summary.inWords = numberToWords(summary.totalAmount);

  return { ...draft, summary };
}

export function updateShowFields(draft: BillDocumentType): BillDocumentType {
  const { settings } = draft;
  if (!has(settings.extraColumnSettings, "quantity")) {
    settings.extraColumnSettings.quantity = true;
  }
  if (!has(settings.extraColumnSettings, "unit")) {
    settings.extraColumnSettings.unit = true;
  }
  const updatedShowFields = [
    settings.extraColumnSettings?.delivered && BillItemFieldTypes.delivered,
    BillItemFieldTypes.description,
    settings.extraColumnSettings.type && BillItemFieldTypes.type,
    settings.extraColumnSettings.department && BillItemFieldTypes.department,
    settings.extraColumnSettings.quantity && BillItemFieldTypes.quantity,
    settings.extraColumnSettings.unit && BillItemFieldTypes.unit,
    BillItemFieldTypes.perUnit,
    BillItemFieldTypes.discountPercent,
    BillItemFieldTypes.discountAmt,
    settings.extraColumnSettings.vat && BillItemFieldTypes.VATPercent,
    BillItemFieldTypes.grossTotal,
    settings.extraColumnSettings.info && BillItemFieldTypes.info
  ].filter((val) => val);
  return {
    ...draft,
    settings: {
      ...settings,
      showFields: updatedShowFields
    }
  };
}

export function calculateCreditNoteBillItems(
  draft: BillDocumentType,
  originalBill: BillType
): BillDocumentType {
  const originalBillItems = originalBill?.document?.billItems;

  const billItems = draft.billItems.map((billItem) => {
    const correspondingItem = originalBillItems.find(({ sNo }) => sNo === billItem.sNo);

    const newBillItem = produce(billItem, (draftItem) => {
      const hasVat = Number(draftItem?.vatPct ?? 0) > 0;

      draftItem.perUnit = round(draftItem.perUnit || 0, 2);
      draftItem.quantity = draftItem.quantity ? -Math.abs(draftItem.quantity) : 0;

      if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.inline) {
        draftItem.discountAmt = round(
          chain(correspondingItem?.discountAmt || 0)
            .divide(correspondingItem?.quantity || 0)
            .multiply(abs(draftItem.quantity))
            .done(),
          2
        );

        // always positive as absolute value of quantity is taken
        // for easy calculations in further steps
        const quantityTimesPerUnitPrice = chain(draftItem.perUnit)
          .multiply(abs(draftItem.quantity))
          .done();

        const itemTaxableAmount = chain(quantityTimesPerUnitPrice)
          .subtract(draftItem.discountAmt)
          .done();

        if (hasVat) {
          draftItem.vatAmtAfterDiscount = round(
            chain(itemTaxableAmount)
              .multiply(draftItem.vatPct ?? 0)
              .divide(100)
              .done(),
            2
          );
        } else {
          draftItem.vatAmtAfterDiscount = 0;
        }

        draftItem.grossTotal = -round(
          chain(itemTaxableAmount)
            .add(hasVat ? draftItem.vatAmtAfterDiscount : 0)
            .done(),
          2
        );
      }
    });

    return newBillItem;
  });

  return { ...draft, billItems };
}

// calculate netReceivableDue after applying credit notes, and refunds against credit notes
export const calculateNetReceivableDueAfterCreditNotes = ({
  originalBill
}: {
  originalBill: BillType;
}): {
  netReceivableDue: number;
} => {
  // total amount which have been credited against credit notes
  let creditNotesSummaryTotal = 0;
  if (originalBill?.creditNotes?.length) {
    creditNotesSummaryTotal = originalBill.creditNotes.reduce(
      (acc, cn) => acc + cn?.summary?.totalAmount || 0,
      0
    );
  }

  // total amount which have been refunded against credit notes
  let totalCreditNoteRefunds = 0;
  if (originalBill?.creditNoteRefunds?.length) {
    totalCreditNoteRefunds = originalBill.creditNoteRefunds.reduce(
      (acc, cn) => acc + cn?.paidAmount || 0,
      0
    );
  }

  const billTotalAfterCreditNotes = chain(originalBill.summary.totalAmount)
    .subtract(Math.abs(creditNotesSummaryTotal))
    .done();

  // it represents how much the client has effectively paid, after adjusting for credit note refunds
  const netPayByClient = chain(originalBill.paymentInfo.paidAmount)
    .subtract(Math.abs(totalCreditNoteRefunds))
    .done();

  // it represents due after credit notes
  const netReceivableDue = billTotalAfterCreditNotes - netPayByClient;

  return { netReceivableDue };
};

export function calculateCreditNoteSummary(draft: BillDocumentType): BillDocumentType {
  const summary: BillSummaryType = {
    taxableAmount: 0,

    // Overall (VAT + HET)
    taxAmount: 0,
    taxableSales: 0,
    taxAmtWithRates: {},

    // VAT
    vatAmount: 0,
    vatTaxableSales: 0,
    vatAmtWithRates: {},

    // other common values
    totalPerUnitTimesQty: 0,
    totalAmount: 0,
    roundOffAmt: Number(draft.summary?.roundOffAmt) || 0,
    inWords: "",
    discount: 0,
    discountPct: 0,
    discountType: draft.summary.discountType,
    exemptSales: 0

    // NOTE: taxableSales and exemptSales were not calculated/put in summary object for CN.
    // I decided to put it in the summary initialized as 0.
    // But since it is a credit note, we dont have to calculate, as CN is not sales.
  };

  let totalBillItemPrice = 0;

  draft.billItems.forEach((billItem) => {
    if (!billItem.returned) return;

    const quantityTimesPerUnitPrice = chain(billItem.perUnit || 0)
      .multiply(abs(billItem.quantity || 0))
      .done();

    totalBillItemPrice = round(chain(totalBillItemPrice).add(quantityTimesPerUnitPrice).done(), 2);

    const hasVat = Number(billItem.vatPct ?? 0) > 0;

    if (hasVat) {
      // tax amount
      const vatAmt = billItem?.vatAmtAfterDiscount || 0;
      summary.taxAmount = round(chain(summary.taxAmount).add(vatAmt).done(), 2);
      summary.vatAmount = round(chain(summary.vatAmount).add(vatAmt).done(), 2);

      // tax by rate
      const vatPct = billItem.vatPct ?? 0;
      summary.taxAmtWithRates[vatPct] =
        round(
          chain(summary.taxAmtWithRates[vatPct] || 0)
            .add(vatAmt)
            .done(),
          2
        ) || 0;

      summary.vatAmtWithRates[vatPct] =
        round(
          chain(summary.vatAmtWithRates[vatPct] || 0)
            .add(vatAmt)
            .done(),
          2
        ) || 0;
    }
  });

  const inlineDiscountSum = draft.billItems.reduce(
    (acc, billItem) => (billItem.returned ? acc + (billItem?.discountAmt || 0) : acc),
    0
  );

  // update summary
  summary.discountPct =
    round(chain(inlineDiscountSum).divide(totalBillItemPrice).done() * 100, 2) || 0;
  summary.discount = inlineDiscountSum;
  summary.totalPerUnitTimesQty = totalBillItemPrice;
  summary.taxableAmount = round(chain(totalBillItemPrice).subtract(summary.discount).done(), 2);

  if (localStorage.getItem("automaticallyCalculateRoundOffAmount") === "true") {
    const totalAmountWithoutRoundoff = round(
      chain(summary.taxableAmount).add(summary.taxAmount).done(),
      2
    );

    const positiveTotalAmount = round(totalAmountWithoutRoundoff, 2);
    summary.roundOffAmt = round(
      chain(positiveTotalAmount).subtract(totalAmountWithoutRoundoff).done(),
      2
    );

    summary.totalAmount = -abs(positiveTotalAmount);
  } else {
    summary.totalAmount = -abs(
      round(
        chain(summary.taxableAmount).add(summary.taxAmount).add(Number(summary.roundOffAmt)).done(),
        2
      )
    );
  }

  summary.inWords = numberToWords(abs(summary.totalAmount));

  return {
    ...draft,
    summary
  };
}

/**
 * Calculates refund info for a credit note based on the original bill and draft.
 *
 * @param {BillDocumentType} params.draft
 * @param {BillType & { summary: BillSummaryType }} params.originalBill
 * @returns {BillDocumentType}
 */
export const calculateCreditNoteRefundAndPaymentInfo = ({
  draft,
  originalBill
}: {
  draft: BillDocumentType;
  originalBill: BillType & { summary: BillSummaryType };
}): BillDocumentType => {
  const { netReceivableDue } = calculateNetReceivableDueAfterCreditNotes({ originalBill });

  const refundInfo = {
    previousPaidAmt: originalBill.paymentInfo.paidAmount,
    previousDueAmt: netReceivableDue,
    maximumRefundable: 0
  };

  refundInfo.maximumRefundable = chain(abs(draft.summary.totalAmount))
    .subtract(refundInfo.previousDueAmt)
    .done();

  // update paymentInfo
  const paymentInfo = { ...draft.paymentInfo };

  const baseMethod = draft.paymentInfo.paymentBreakdown?.[0]?.method || paymentOptionsEnum.cash;
  const method = baseMethod === paymentOptionsEnum.balance ? paymentOptionsEnum.cash : baseMethod;

  if (refundInfo?.maximumRefundable > 0) {
    paymentInfo.paid = true;
    paymentInfo.paidAmount = refundInfo.maximumRefundable;
    paymentInfo.paymentBreakdown = [
      {
        method,
        amount: refundInfo?.maximumRefundable
      }
    ];
  } else {
    paymentInfo.paid = false;
    paymentInfo.paidAmount = 0;
    paymentInfo.paymentBreakdown = [{ method: paymentOptionsEnum.cash, amount: 0 }];
  }

  paymentInfo.paidAmount = -paymentInfo.paidAmount;

  return {
    ...draft,
    refundInfo,
    paymentInfo
  };
};

// adjust payment breakdown if the totalAmount < sum of amount in paymentBreakdown
function adjustPaymentBreakdownDown({
  paymentBreakdown,
  totalAmount,
  clientBalance
}: {
  paymentBreakdown: Array<PaymentBreakdown>;
  totalAmount: number;
  clientBalance: number;
}): Array<PaymentBreakdown> {
  let currentBreakdown = [...paymentBreakdown];

  // 1. Move balance to the END so it is decreased last
  currentBreakdown.sort((a, b) => {
    if (a.method === paymentOptionsEnum.balance) return -1;
    if (b.method === paymentOptionsEnum.balance) return 1;
    return 0;
  });

  // 2. Calculate current total
  let currentTotal = currentBreakdown.reduce((sum, m) => sum + m.amount, 0);

  // 3. If already fine, still cap balance
  if (currentTotal <= totalAmount) {
    return currentBreakdown.map((m) => ({
      ...m,
      amount: m.method === paymentOptionsEnum.balance ? Math.min(m.amount, clientBalance) : m.amount
    }));
  }

  // 4. We need to shrink from the end
  // eslint-disable-next-line no-plusplus
  for (let i = currentBreakdown.length - 1; i >= 0 && currentTotal > totalAmount; i--) {
    const m = currentBreakdown[i];
    const allowed = currentTotal - totalAmount;

    if (m.amount > allowed) {
      m.amount -= allowed;
      currentTotal = totalAmount;
      break;
    } else {
      currentTotal -= m.amount;
      m.amount = 0;
    }
  }

  // 5. Remove zero-amount currentBreakdown
  currentBreakdown = currentBreakdown.filter((m) => m.amount > 0);

  // 6. Cap balance to clientBalance
  currentBreakdown = currentBreakdown.map((m) => {
    if (m.method === paymentOptionsEnum.balance) {
      return { ...m, amount: Math.min(m.amount, clientBalance) };
    }
    return m;
  });

  return currentBreakdown;
}

/**
 *
 * @param {BillDocumentType} params.draft
 * @returns {BillDocumentType}
 *
 * @info
 * only used for invoice but not credit note
 */
export const distributePayment = (draft: BillDocumentType): BillDocumentType => {
  const paymentInfo = cloneDeep(draft.paymentInfo);
  const totalAmount = draft.summary.totalAmount || 0;

  // ssf billing
  if (draft.ssf?.id && draft.client?.id) {
    // TODO: handle paymentBreakdown in ssf billing
    const { balance: blc, scheme, subProduct } = draft.ssf;
    const isMedicalCase = scheme.value === SCHEME_TYPE.MEDICAL.value;
    let ssfBalance = blc.accident.value;
    if (isMedicalCase) {
      if (subProduct.value === MEDICAL_SUB_PRODUCT.OPD.value) {
        ssfBalance = blc.medical.opd;
      } else {
        ssfBalance = blc.medical.ipd + blc.medical.opd;
      }
    }

    const coveragePercent = isMedicalCase ? 80 : 100;
    const totalAmountToCoverBySsf = totalAmount * (coveragePercent / 100);
    if (totalAmountToCoverBySsf <= ssfBalance) {
      const paidAmtByPatient = totalAmount - totalAmountToCoverBySsf;
      if (paymentInfo.paid === true) {
        paymentInfo.paidAmount = paidAmtByPatient;
        paymentInfo.paymentDistribution = {
          paymentFromSsf: totalAmountToCoverBySsf,
          paymentFromBalance: 0,
          additionalPayment: paidAmtByPatient
        };
      } else {
        paymentInfo.paidAmount = 0;
        paymentInfo.paymentDistribution = {
          paymentFromSsf: totalAmountToCoverBySsf,
          paymentFromBalance: 0,
          additionalPayment: 0
        };
      }
    } else {
      const paidAmtByPatient = totalAmount - ssfBalance;
      paymentInfo.paidAmount = paidAmtByPatient;
      paymentInfo.paymentDistribution = {
        paymentFromSsf: ssfBalance,
        paymentFromBalance: 0,
        additionalPayment: paidAmtByPatient
      };
    }
  } else {
    // else normal billing
    const { balance = 0 } = draft.client;

    if (balance > 0) {
      if (balance >= totalAmount) {
        paymentInfo.paid = true;
        paymentInfo.paidAmount = totalAmount;
        paymentInfo.paymentBreakdown = [
          { method: paymentOptionsEnum.balance, amount: totalAmount }
        ];

        paymentInfo.paymentDistribution = {
          ...paymentInfo.paymentDistribution,
          paymentFromSsf: 0,
          additionalPayment: 0,
          paymentFromBalance: paymentInfo.paidAmount
        };
      }

      if (balance < totalAmount) {
        const rawPaidAmount = paymentInfo.paymentBreakdown.reduce((sum, p) => sum + p.amount, 0);

        // If bill total is now smaller, normalize
        if (rawPaidAmount > totalAmount) {
          paymentInfo.paymentBreakdown = adjustPaymentBreakdownDown({
            paymentBreakdown: paymentInfo.paymentBreakdown,
            totalAmount,
            clientBalance: balance
          });
        }

        // this will confirm the amount in balance will always be equal to clients balance when
        // the client balance is smaller than the totalAmount.
        // It also confirms balance is used up for payment incase client has balance
        paymentInfo.paymentBreakdown = paymentInfo.paymentBreakdown.map((p) =>
          p.method === paymentOptionsEnum.balance ? { ...p, amount: balance } : p
        );

        // Recalculate using NEW breakdown
        const paidAmount = paymentInfo.paymentBreakdown.reduce((sum, p) => sum + p.amount, 0);
        const additionalPayment = paymentInfo.paymentBreakdown
          .filter((p) => p.method !== paymentOptionsEnum.balance)
          .reduce((acc, curr) => curr.amount + acc, 0);

        paymentInfo.paidAmount = paidAmount;
        paymentInfo.paid = paidAmount >= totalAmount;

        paymentInfo.paymentDistribution = {
          paymentFromSsf: 0,
          additionalPayment,
          paymentFromBalance: balance
        };
      }
    } else {
      // if there is balance, remove it because this is no balance else block
      // if current client has balance, and we change client who has no balance, his is needed
      if (paymentInfo.paymentBreakdown.some((p) => p.method === paymentOptionsEnum.balance)) {
        paymentInfo.paymentBreakdown = [{ method: paymentOptionsEnum.cash, amount: 0 }];
      }

      const rawPaidAmount = paymentInfo.paymentBreakdown.reduce((sum, p) => sum + p.amount, 0);

      // If bill total is smaller, normalize
      if (rawPaidAmount > totalAmount) {
        paymentInfo.paymentBreakdown = adjustPaymentBreakdownDown({
          paymentBreakdown: paymentInfo.paymentBreakdown,
          totalAmount,
          clientBalance: 0
        });
      }

      // Recalculate using NEW breakdown
      const paidAmount = paymentInfo.paymentBreakdown.reduce((sum, p) => sum + p.amount, 0);
      const additionalPayment = paymentInfo.paymentBreakdown
        .filter((p) => p.method !== paymentOptionsEnum.balance || p.amount > 0)
        .reduce((acc, curr) => curr.amount + acc, 0);

      paymentInfo.paidAmount = paidAmount;
      paymentInfo.paid = paidAmount >= totalAmount;

      paymentInfo.paymentDistribution = {
        paymentFromSsf: 0,
        additionalPayment,
        paymentFromBalance: 0
      };
    }
  }

  return { ...draft, paymentInfo };
};

export const reformDraftData = (draft: BillDocumentType): BillDocumentType => {
  // Credit Note, remove items that are not returned
  if (draft.type === DocumentTypes.CREDITNOTE) {
    // might cause a problem, since updating draft may
    // change (not sure) total amount or refundable amount
    // so, if refunded amount is less than max. refundable,
    // there is possibility that refunded amount again gets reset to max. refundable
    // not sure, will check once multiple payment for credit note comes
    // and refunded amount is allowed to be edited
    draft.billItems = draft.billItems.filter((item) => item.returned);
  }

  if (draft.paymentInfo.paidAmount === 0) {
    draft.paymentInfo.paymentBreakdown = [];
  } else {
    draft.paymentInfo.paymentBreakdown = draft.paymentInfo?.paymentBreakdown.filter(
      (p) => Number(p.amount) > 0
    );
  }

  return draft;
};

export const mergeBatchInfo = (
  billItems: Array<BillItemType & { batchId: string }> = []
): Array<Partial<BillItemType>> =>
  billItems.map((billItem) => {
    let source;
    if (billItem.batchId) {
      source = BillItemSource.stocks;
    } else {
      source = BillItemSource.services;
    }
    return omit(
      {
        ...billItem,
        batchInfo: {
          quantity: billItem.quantity,
          batchId: billItem.batchId || "",
          expiryDate: billItem?.expiryDate
        },
        ...(!billItem.source ? { source } : {})
      },
      "batchId"
    );
  });

export const splitBatchInfo = (billItems: Array<BillItemType> = []): Array<Partial<BillItemType>> =>
  billItems.map((item) =>
    omit(
      {
        ...item,
        ...(item.batchInfo?.batchId ? { batchId: item.batchInfo.batchId } : {}),
        ...{ expiryDate: item.batchInfo?.expiryDate }
      },
      ["batchInfo"]
    )
  );

export function mergeServicesProducts(
  services: Array<ProductInterface>,
  products: Array<StockProducts>,
  isSsfBilling: boolean
): Array<Record<string, unknown>> {
  return [
    ...services
      ?.filter(({ active }) => active)
      .map((service) =>
        omit(
          {
            ...service,
            structuredInfo: service.info,
            source: BillItemSource.services
          },
          "info"
        )
      ),
    ...products
      ?.filter(({ active, productType }) => active && productType === 1)
      .map((item) => ({
        ...item,
        source: BillItemSource.stocks,
        ...(isSsfBilling ? { code: SSF_MEDICINE_CODE } : {})
      }))
  ];
}

export const hasFiscalYear2079Started = (): boolean => {
  // check if fiscal 2079 has started
  const today = moment();
  const fiscalYear2079 = moment(convertBStoAD(2079, 4, 1));

  return fiscalYear2079.isBefore(today);
};

export const showUnitAfterQuantity = (column: BillColumnsType): boolean =>
  column === BillColumns.QUANTITY;

export const getBillNumberType = (draft: BillDocumentType): string => {
  let billNumberType;

  if (draft.isInternal) {
    // check if fiscal 2079 has started
    if (!hasFiscalYear2079Started()) {
      billNumberType = "internalBilling";
    } else if (draft.type === DocumentTypes.INVOICE) {
      billNumberType = TrackingNumType.SALES;
    } else {
      billNumberType = TrackingNumType.CREDIT_NOTE;
    }
  } else {
    billNumberType = "billing";
  }

  return billNumberType;
};

export const marginForNameColumn = (column: BillColumns): number =>
  column === BillColumns.DESCRIPTION ? 1 : 0;

export const showInfoColumn = (billData: BillType): boolean =>
  // show info column if any item of bill isNarcotics or if enabled from column settings
  (billData.document.billItems || []).some((item) => item.productData?.isNarcotics) ||
  Boolean(billData.document.settings.extraColumnSettings?.info);

// show hsCode column if any item of bill has hsCode
export const hasItemWithHSCode = (billData: BillType): boolean =>
  (billData.document.billItems || []).some((item) => item.hsCode);

export const getColumnTextStyles = ({
  column,
  index,
  fontSize = scaleFont("0.33cm")
}: {
  fontSize?: string;
  index: number;
  column: BillColumns;
}): React.CSSProperties => {
  let textAlignment = index > 2 ? "center" : "left";
  if (column === BillColumns.GROSS_TOTAL || column === BillColumns.PER_UNIT) {
    textAlignment = "right";
  }
  return {
    pageBreakInside: "avoid",
    fontWeight: [BillColumns.DESCRIPTION, BillColumns.GROSS_TOTAL].includes(column) ? 500 : 400,
    fontSize,
    textAlign: textAlignment,
    whiteSpace: column === BillColumns.INFO ? "pre" : "normal"
  };
};

export const getPaymentOptions = (
  draft: BillDocumentType,
  isCreditNote: boolean
): {
  enablePaymentSelection: boolean;
  paymentMethodList: string[];
  selectedPaymentMethod: string;
} => {
  let enablePaymentSelection = true;
  let paymentMethodList = paymentOptions;
  let selectedPaymentMethod = draft.paymentInfo?.paymentBreakdown?.[0].method ?? "";

  if (draft && isCreditNote) {
    const { previousPaidAmt, maximumRefundable } = draft.refundInfo as RefundInfoType;

    paymentMethodList = paymentOptions.filter((option) => option !== paymentOptionsEnum.balance);

    if (previousPaidAmt === 0 || maximumRefundable <= 0) {
      enablePaymentSelection = false;
    }

    if (maximumRefundable > 0) {
      enablePaymentSelection = true;

      selectedPaymentMethod =
        selectedPaymentMethod === paymentOptionsEnum.noPayment
          ? paymentOptionsEnum.cash
          : selectedPaymentMethod;

      paymentMethodList = paymentMethodList.filter(
        (option) => option !== paymentOptionsEnum.noPayment && option !== paymentOptionsEnum.balance
      );
    }
  } else {
    paymentMethodList = paymentOptions.filter((option) => {
      if (option === paymentOptionsEnum.noPayment) {
        return false;
      }

      if (
        option === paymentOptionsEnum.balance &&
        (draft?.client?.balance ?? 0) < draft.summary.totalAmount
      ) {
        return false;
      }

      return true;
    });
  }

  return { enablePaymentSelection, paymentMethodList, selectedPaymentMethod };
};

export const hasServiceProvider = (bill: BillType): boolean => {
  // If any bill item has serviceProviderId or any subItem does
  const hasSP = bill.document.billItems.some(
    (item) =>
      item?.serviceProviderId ||
      item?.serviceProviderIds?.length ||
      item.subItems?.some((sub) => sub?.serviceProviderId) ||
      item.subItems?.some((sub) => sub?.serviceProviderIds?.length)
  );

  return hasSP && bill.type === DocumentTypes.INVOICE;
};

// function to convert old bill with paymentInfo.paymentMethod to new paymentBreakdown array
export const transformPaymentInfo = (info: BillPaymentInfoType): BillPaymentInfoType => {
  // if already a newer, return as-is
  if (info?.paymentBreakdown?.length > 0) {
    return info;
  }

  // fallback: convert old paymentMethod to new breakdown
  const paymentMethod =
    info?.paymentMethod === paymentOptionsEnum.noPayment
      ? paymentOptionsEnum.cash
      : info?.paymentMethod || paymentOptionsEnum.cash;

  const amountPaid = info.paidAmount || 0;

  // return a new object with updated fields by stripping the strip legacy field
  const rest = omit(info, "paymentMethod");
  return {
    ...rest,
    paymentBreakdown: [{ method: paymentMethod, amount: amountPaid }]
  };
};

// get bull item key for creditable quantity
export const getBillItemKeyForCreditbleQuantity = (item: Partial<BillItemType>): string => {
  const baseId = item?.productId || item?.productData?.id || item?.description;

  const parts = [baseId];

  if (item?.batchInfo?.batchId) {
    parts.push(item.batchInfo?.batchId);
  }

  if (item?.uuid) {
    parts.push(item.uuid);
  }

  return parts.join("::");
};
